import type { ReactElement } from "react";
import { Check, Plus, X as XIcon } from "lucide-react";
import { space } from "@tokens/index";
import { Button } from "../../primitives";
import { Collapse } from "./Collapse";
import { ComposerField } from "./ComposerField";
import type { NoteComposerState } from "./useNoteComposer";

/** `+ Add note` / `Cancel` trigger. */
export function NoteComposerTrigger({
  composer,
  size = "sm",
  variant = "ghost",
}: {
  readonly composer: NoteComposerState;
  readonly size?: "sm" | "md";
  readonly variant?: "ghost" | "secondary";
}): ReactElement {
  return (
    <Button
      ref={composer.triggerRef}
      variant={variant}
      size={size}
      aria-label={composer.open ? "Cancel note" : "Add note"}
      aria-expanded={composer.open}
      onClick={composer.toggle}
    >
      {composer.open ? (
        <XIcon size={14} strokeWidth={2.5} aria-hidden="true" />
      ) : (
        <Plus size={14} strokeWidth={2.5} aria-hidden="true" />
      )}
      {composer.open ? "Cancel" : "Add note"}
    </Button>
  );
}

/** The field row. Enter saves, Shift+Enter breaks a line, Escape cancels. */
export function NoteComposerRow({
  composer,
  placeholder,
  disabled = false,
}: {
  readonly composer: NoteComposerState;
  readonly placeholder: string;
  readonly disabled?: boolean;
}): ReactElement {
  return (
    <Collapse open={composer.open} testId="note-composer">
      <div
        style={{
          display: "flex",
          gap: space[2],
          alignItems: "flex-start",
          padding: `0 ${space[4]} ${space[3]}`,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <ComposerField
            autoFocus
            value={composer.draft}
            onChange={composer.setDraft}
            onSubmit={composer.submit}
            onCancel={composer.close}
            placeholder={placeholder}
            ariaLabel="Note body"
            disabled={disabled}
          />
        </div>
        <Button
          variant="primary"
          size="md"
          aria-label="Save note"
          disabled={!composer.canSubmit || disabled}
          onClick={composer.submit}
          style={{ flexShrink: 0 }}
        >
          <Check size={14} strokeWidth={2.5} aria-hidden="true" />
          Save
        </Button>
      </div>
    </Collapse>
  );
}
